import axios from "axios";
import useLogin from "hooks/useLogin";
import { URL_ROLE } from "constants/urlApis";
import { endpointError } from "utils/codeErrorsTimeout";
import { isNullOrUndefined } from "utils/validations";

export default function useRoles() {
  const { jwt } = useLogin();
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };

  /**
   * Retrieves a paginated list of users and roles based on a search term.
   * This function sends a GET request to fetch users and roles with pagination
   * and filtering based on the provided search term.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page.
   * @param {string} searching - The search term to filter users and roles by name.
   * @returns {Promise<Object>} A promise that resolves to the data containing the list of users and roles.
   */
  const getAllUserAndRoles = async (page, per_page, searching) => {
    const response = await axios.get(
      `${URL_ROLE}/roles/?name=${searching}&page=${page}&per_page=${per_page}`,
      {
        headers,
        endpointError: endpointError.roles.getAllUserAndRoles,
      }
    );
    return response.data;
  };

  /**
   * Creates a new role with the specified name.
   * This function sends a POST request to create a new role in the system with the given name.
   * @param {string} name - The name of the role to be created.
   * @returns {Promise<Object>} A promise that resolves to the response object from the API call.
   */
  const createRole = async (name) => {
    const response = await axios.post(`${URL_ROLE}/roles/`, name, {
      headers,
      endpointError: endpointError.roles.createRole,
    });
    return response;
  };

  /**
   * Retrieves the details of a specific role by its ID.
   * This function sends a GET request to fetch the role details based on the provided role ID.
   * @param {string} id - The ID of the role to be retrieved.
   * @returns {Promise<Object>} A promise that resolves to the role details.
   */
  const getRole = async (id) => {
    const response = await axios.get(`${URL_ROLE}/roles/${id}`, {
      headers,
      endpointError: endpointError.roles.getRole,
    });
    return response.data;
  };

  /**
   * Updates the details of a specific role by its ID.
   * This function sends a PATCH request to update the role details based on the provided role ID and new name.
   * @param {string} id - The ID of the role to be updated.
   * @param {string} name - The new name for the role.
   * @returns {Promise<Object>} A promise that resolves to the updated role details.
   */
  const editRole = async (id, name) => {
    const response = await axios.patch(`${URL_ROLE}/roles/${id}/`, name, {
      headers,
      endpointError: endpointError.roles.editRole,
    });
    return response;
  };

  /**
   * Creates a new role with the specified name.
   * This function sends a POST request to create a new role in the system with the given name.
   * @param {string} name - The name of the role to be created.
   * @returns {Promise<Object>} A promise that resolves to the response object from the API call.
   */
  const deleteRole = async (id) => {
    const response = await axios.delete(`${URL_ROLE}/roles/${id}`, {
      headers,
      endpointError: endpointError.roles.deleteRole,
    });
    return response;
  };

  /**
   * Retrieves users associated with a specific role with pagination and search filters.
   * This function sends a GET request to fetch users that have the given role ID,
   * supporting pagination and filtering by user_name, first_name, and last_name.
   * @param {string} idRol - The ID of the role for which users are to be fetched.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page.
   * @param {string} search - The search term to filter users by user_name, first_name, and last_name.
   * @returns {Promise<Object>} A promise that resolves to the response containing the list of users with the specified role.
   */
  const getUsersByRol = async (idRol, page, per_page, search) => {
    if (
      isNullOrUndefined(search) === false &&
      isNullOrUndefined(page) === false &&
      isNullOrUndefined(per_page) === false
    ) {
      const response = await axios.get(
        `${URL_ROLE}/users/roles/${idRol}/?user_name=${search}&first_name=${search}&last_name=${search}&page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.roles.getUsersByRol,
        }
      );
      return response;
    }

    const response = await axios.get(
      `${URL_ROLE}/users/roles/${idRol}/?page=${page}&per_page=${per_page}`,
      {
        headers,
        endpointError: endpointError.roles.getUsersByRol,
      }
    );
    return response;
  };

  /**
   * Retrieves members associated with a specific role.
   * This function sends a GET request to fetch members of a role, supporting pagination.
   * @param {string} idRol - The ID of the role for which members are to be fetched.
   * @param {number} page - The page number to retrieve.
   * @param {number} per_page - The number of members per page.
   * @returns {Promise<Object>} A promise that resolves to the response containing the list of members for the specified role.
   */
  const getMembers = async (idRol, page, per_page) => {
    const response = await axios.get(
      `${URL_ROLE}/roles/${idRol}/members/?page=${page}&per_page=${per_page}`,
      {
        headers,
        endpointError: endpointError.roles.getMembers,
      }
    );
    return response;
  };

  /**
   * Adds members to a specific role.
   * This function sends a POST request to add new members to a role.
   * @param {string} idRol - The ID of the role to which members are to be added.
   * @param {Object[]} addMembers - An array of objects representing the members to be added.
   * @param {string} addMembers[].id - The ID of the member to be added.
   * @param {string} [addMembers[].name] - The name of the member to be added (optional).
   * @returns {Promise<Object>} A promise that resolves to the response containing details of the added members.
   */
  const addMembersByRol = async (idRol, addMembers) => {
    const response = await axios.post(`${URL_ROLE}/roles/${idRol}/members/`, addMembers, {
      headers,
      endpointError: endpointError.roles.addMembersByRol,
    });
    return response;
  };

  /**
   * Removes a member from a specific role.
   * This function sends a DELETE request to remove a member from a role.
   * @param {string} idRol - The ID of the role from which the member is to be removed.
   * @param {string} idMember - The ID of the member to be removed from the role.
   * @returns {Promise<Object>} A promise that resolves to the response confirming the removal.
   */
  const deleteMember = async (idRol, idMember) => {
    const response = await axios.delete(`${URL_ROLE}/roles/${idRol}/members/${idMember}`, {
      headers,
      endpointError: endpointError.roles.deleteMember,
    });
    return response;
  };

  /**
   * Retrieves a list of forms associated with a specific role.
   * This function sends a GET request to fetch all forms related to a given role ID.
   * @param {string} id - The ID of the role whose associated forms are to be retrieved.
   * @returns {Promise<Object>} A promise that resolves to the response containing the list of forms associated with the role.
   */
  const getFormsByRol = async (id) => {
    const response = await axios.get(`${URL_ROLE}/roles/${id}/forms/`, {
      headers,
      endpointError: endpointError.roles.getFormsByRol,
    });
    return response;
  };

  /**
   * Adds permissions to a role by its ID.
   * @param {string} id - The ID of the role.
   * @param {Object} permissionsForm - The form data containing permissions.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const addPermissionsByRol = async (id, permissionsForm) => {
    const response = await axios.post(
      `${URL_ROLE}/roles/${id}/form-permissions/`,
      permissionsForm,
      {
        headers,
        endpointError: endpointError.roles.addPermissionsByRol,
      }
    );
    return response;
  };

  /**
   * Updates permissions for a role by its ID.
   * @param {string} id - The ID of the role.
   * @param {Object} permissionsForm - The form data containing updated permissions.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const updatePermissionsByRol = async (id, permissionsForm) => {
    const response = await axios.patch(
      `${URL_ROLE}/roles/${id}/form-permissions/`,
      permissionsForm,
      {
        headers,
        endpointError: endpointError.roles.updatePermissionsByRol,
      }
    );
    return response;
  };

  /**
   * Adds permissions configuration to a role by its ID.
   * @param {string} id - The ID of the role.
   * @param {Object} permissionsConfig - The configuration data containing permissions.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const addPermissionsConfig = async (id, permissionsConfig) => {
    const response = await axios.post(`${URL_ROLE}/roles/${id}/permissions/`, permissionsConfig, {
      headers,
      endpointError: endpointError.roles.addPermissionsConfig,
    });
    return response;
  };

  /**
   * Adds designer form permissions to a role by its ID.
   * @param {string} rolId - The ID of the role.
   * @param {Object} permissionsDesignerForms - The form data containing designer permissions.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const addPermissionsDesignerForms = async (rolId, permissionsDesignerForms) => {
    const response = await axios.post(
      `${URL_ROLE}/roles/${rolId}/admin-forms/`,
      permissionsDesignerForms,
      {
        headers,
        endpointError: endpointError.roles.addPermissionsDesignerForms,
      }
    );
    return response;
  };

  /**
   * Adds designer workflow permissions to a role by its ID.
   * @param {string} rolId - The ID of the role.
   * @param {Object} permissionsDesignerWorkflows - The workflow data containing designer permissions.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const addPermissionsDesignerWorkflows = async (rolId, permissionsDesignerWorkflows) => {
    const response = await axios.post(
      `${URL_ROLE}/roles/${rolId}/admin-workflows/`,
      permissionsDesignerWorkflows,
      {
        headers,
        endpointError: endpointError.roles.addPermissionsDesignerWorkflows,
      }
    );
    return response;
  };

  /**
   * Retrieves form permissions for a role by its ID with pagination.
   * @param {string} id - The ID of the role.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const getPermissionsFormsByRol = async (id, page, per_page) => {
    const response = await axios.get(
      `${URL_ROLE}/roles/${id}/form-permissions/?page=${page}&per_page=${per_page}`,
      {
        headers,
        endpointError: endpointError.roles.getPermissionsFormsByRol,
      }
    );
    return response;
  };

  /**
   * Retrieves the permissions configuration for a role by its ID.
   * @param {string} id - The ID of the role.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const getPermissionsConfigByRol = async (id) => {
    const response = await axios.get(`${URL_ROLE}/roles/${id}/permissions/`, {
      headers,
      endpointError: endpointError.roles.getPermissionsConfigByRol,
    });
    return response;
  };

  /**
   * Retrieves the admin form permissions for a role by its ID.
   * @param {string} id - The ID of the role.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const getPermissionsAdminFormsByRol = async (id) => {
    const response = await axios.get(`${URL_ROLE}/roles/${id}/admin-forms/`, {
      headers,
      endpointError: endpointError.roles.getPermissionsAdminFormsByRol,
    });
    return response;
  };

  /**
   * Retrieves the admin workflow permissions for a role by its ID.
   * @param {string} id - The ID of the role.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const getPermissionsAdminWorkflowsByRol = async (id) => {
    const response = await axios.get(`${URL_ROLE}/roles/${id}/admin-workflows/`, {
      headers,
      endpointError: endpointError.roles.getPermissionsAdminWorkflowsByRol,
    });
    return response;
  };

  /**
   * Deletes form permissions for a role by its ID and form ID.
   * @param {string} uuidRol - The UUID of the role.
   * @param {string} uuidForm - The UUID of the form.
   * @returns {Promise<Object>} The response from the server.
   * @throws Will throw an error if the request fails.
   */
  const deleteFormPremissionByRol = async (uuidRol, uuidForm) => {
    const response = await axios.delete(
      `${URL_ROLE}/roles/${uuidRol}/forms/${uuidForm}/form-permissions/`,
      {
        headers,
        endpointError: endpointError.roles.deleteFormPremissionByRol,
      }
    );
    return response;
  };

  /**
   * Sends a POST request to the server to update the permissions for a specific role's TRD levels.
   * @param {string} rolId - The ID of the role for which the TRD level permissions are being updated.
   * @param {Object} levelTrdPermissions - The permissions to be updated for the TRD levels.
   * @returns {Promise} A promise that resolves with the server response after updating the permissions.
   */
  const levelsTrdPermissions = async (rolId, levelTrdPermissions) => {
    const response = await axios.post(
      `${URL_ROLE}/roles/${rolId}/levels-trd-permissions/`,
      levelTrdPermissions,
      {
        headers,
        endpointError: endpointError.roles.levelsTrdPermissions,
      }
    );
    return response;
  };

  /**
   * Retrieves TRD permissions based on the role UUID.
   * @param {string} rolUuid - The UUID of the role to retrieve permissions for.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page for pagination.
   * @returns {Promise} - The response containing TRD permissions for the specified role UUID.
   */
  const getTRDPermissionsbyRoleUUID = async (rolUuid, page, per_page) => {
    const response = await axios.get(
      `${URL_ROLE}/roles/${rolUuid}/levels-trd-permissions/?page=${page}&per_page=${per_page}`,
      {
        headers,
        endpointError: endpointError.roles.getTRDPermissionsbyRoleUUID,
      }
    );
    return response.data;
  };

  /**
   * Updates TRD permissions for a specific role and level based on the provided role UUID, level UUID, and permissions level.
   * @param {string} rolUuid - The UUID of the role to update permissions for.
   * @param {string} levelUuid - The UUID of the level to update permissions for.
   * @param {object} permissionsLevel - The permissions level object to update.
   * @returns {Promise} - A promise that resolves with the response from the PATCH request.
   */
  const updateTRDPermissionsbyRoleUUID = async (rolUuid, levelUuid, permissionsLevel) => {
    const response = await axios.patch(
      `${URL_ROLE}/roles/${rolUuid}/levels-trd-permissions/${levelUuid}/`,
      permissionsLevel,
      {
        headers,
        endpointError: endpointError.roles.updateTRDPermissionsbyRoleUUID,
      }
    );
    return response;
  };

  return {
    getAllUserAndRoles,
    createRole,
    getRole,
    editRole,
    deleteRole,
    getUsersByRol,
    getMembers,
    addMembersByRol,
    deleteMember,
    getFormsByRol,
    addPermissionsByRol,
    addPermissionsConfig,
    addPermissionsDesignerForms,
    addPermissionsDesignerWorkflows,
    getPermissionsFormsByRol,
    getPermissionsConfigByRol,
    getPermissionsAdminFormsByRol,
    getPermissionsAdminWorkflowsByRol,
    updatePermissionsByRol,
    deleteFormPremissionByRol,
    levelsTrdPermissions,
    getTRDPermissionsbyRoleUUID,
    updateTRDPermissionsbyRoleUUID,
  };
}
